/**
 * Timezone utilities using native Intl APIs
 * Replaces moment-timezone for timezone operations
 */
import { format } from 'date-fns';

/**
 * Formats a Date's *local calendar fields* as a wall-clock string with no zone
 * designator: "2026-09-29T09:00:00".
 *
 * The date pickers build a Date whose local fields hold exactly what the admin
 * typed, so these fields — not the instant the Date represents — are the value
 * we mean. Sending them with the selected IANA zone lets the server do the one
 * conversion, instead of the browser guessing with its own zone (DVLP-7797).
 */
export const toWallClockString = (date: Date): string => format(date, "yyyy-MM-dd'T'HH:mm:ss");

/**
 * Detects the user's current timezone using native browser APIs
 * Equivalent to moment.tz.guess()
 */
export const guessTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Extended Intl interface for supportedValuesOf
 * This method is available in modern browsers (Chrome 93+, Firefox 93+, Safari 15.4+)
 */
interface IntlWithSupportedValuesOf {
  supportedValuesOf(key: 'timeZone'): string[];
}

/**
 * Returns a list of all IANA timezone names
 * Equivalent to moment.tz.names()
 *
 * Uses Intl.supportedValuesOf('timeZone') in modern browsers,
 * falls back to a curated list for older browsers
 */
export const getTimezoneNames = (): string[] => {
  if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl) {
    return (Intl as unknown as IntlWithSupportedValuesOf).supportedValuesOf('timeZone');
  }
  // Fallback for older browsers
  return FALLBACK_TIMEZONES;
};

/**
 * Formats a date with timezone information
 * Equivalent to moment().tz(timezone).format('ddd, MMM D @ h:mmA z')
 */
export const formatDateWithTimezone = (date: Date, timezone?: string): string => {
  const tz = timezone ?? guessTimezone();

  const formatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: tz,
    timeZoneName: 'short',
  });

  const parts = formatter.formatToParts(date);
  const getPart = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';

  // Format: "Mon, Jan 1 @ 2:30PM EST"
  const weekday = getPart('weekday');
  const month = getPart('month');
  const day = getPart('day');
  const hour = getPart('hour');
  const minute = getPart('minute');
  const dayPeriod = getPart('dayPeriod');
  const timeZoneName = getPart('timeZoneName');

  return `${weekday}, ${month} ${day} @ ${hour}:${minute}${dayPeriod} ${timeZoneName}`;
};

/**
 * Fallback timezone list for browsers that don't support Intl.supportedValuesOf
 * This is a curated list of common IANA timezones
 */
const FALLBACK_TIMEZONES = [
  'Africa/Abidjan',
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'America/Anchorage',
  'America/Argentina/Buenos_Aires',
  'America/Bogota',
  'America/Chicago',
  'America/Denver',
  'America/Edmonton',
  'America/Halifax',
  'America/Lima',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/New_York',
  'America/Phoenix',
  'America/Santiago',
  'America/Sao_Paulo',
  'America/St_Johns',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Baghdad',
  'Asia/Bangkok',
  'Asia/Colombo',
  'Asia/Dhaka',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Istanbul',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Kuala_Lumpur',
  'Asia/Kuwait',
  'Asia/Manila',
  'Asia/Riyadh',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Taipei',
  'Asia/Tehran',
  'Asia/Tokyo',
  'Atlantic/Azores',
  'Atlantic/Reykjavik',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Darwin',
  'Australia/Hobart',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Athens',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Budapest',
  'Europe/Copenhagen',
  'Europe/Dublin',
  'Europe/Helsinki',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Moscow',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Prague',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Vienna',
  'Europe/Warsaw',
  'Europe/Zurich',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Guam',
  'Pacific/Honolulu',
  'Pacific/Samoa',
  'UTC',
];
